import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageMeta } from "../lib/usePageMeta";
import { api } from "../lib/api";
import SchoolQuoteModal from "../components/SchoolQuoteModal";
import CruzeiroCard from "../components/CruzeiroCard";
import CruzeiroLightbox from "../components/CruzeiroLightbox";
import {
  Ship, Anchor, MapPin, ShieldCheck, ClipboardList, HeartHandshake,
  Clock, Wallet, ArrowRight, Bus, Star, Plane,
} from "lucide-react";
import "./Portfolio.css";
import "./Cruzeiros.css";

const HERO_IMG = "https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&w=1600&q=70";

// Pilares do serviço — herdados dos briefs de conteúdo Orion (mesma linha de Intercâmbio)
const PILARES = [
  {
    icon: ShieldCheck,
    title: "Contratação segura",
    body: "Cadastur ativo, contrato formal, cobertura de garantia e nota fiscal. Só trabalhamos com companhias oficiais (MSC, Costa, Royal Caribbean, Norwegian, Disney).",
  },
  {
    icon: HeartHandshake,
    title: "Referência no segmento",
    body: "Equipe própria de consultores em cruzeiros. Filtramos evento a bordo (temático, gospel, sertanejo, LGBT+, corporativo) pra garantir que combine com você.",
  },
  {
    icon: Bus,
    title: "Transfer SP → Santos",
    body: "Ida e volta com van executiva Wi-Fi, motorista uniformizado, saída de Zona Sul, Leste, Oeste, Norte e ABC. Horário sincronizado com o check-in do navio.",
  },
  {
    icon: Clock,
    title: "Suporte 24h no navio",
    body: "Consultor no WhatsApp em português durante toda a viagem. Casos reais: passaporte perdido, voo cancelado, troca de cabine — tudo resolvido a bordo.",
  },
];

// Como funciona — 4 passos (mesmo padrão da página de Intercâmbio)
const HOW = [
  { step: "01", title: "Consultoria Gratuita", body: "Conversamos por WhatsApp/Zoom pra entender o perfil (família, casal, 3ª idade, temático) e o orçamento. Devolvemos 2 a 3 opções comparadas." },
  { step: "02", title: "Reserva & Contrato", body: "Fechamos a cabine, emitimos contrato formal, boleto de sinal e cronograma de pagamento. Sem letras miúdas — checklist assinado incluso." },
  { step: "03", title: "Preparação Pré-embarque", body: "Documentação (passaporte, RG novo), seguro-viagem, transfer confirmado, briefing de embarque e do que esperar no navio." },
  { step: "04", title: "Suporte durante e depois", body: "Transfer no dia D, consultor de plantão pelo WhatsApp durante toda a viagem, retorno com transfer Santos → SP e follow-up pós-viagem." },
];

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo", body: "Ministério do Turismo." },
  { icon: ClipboardList, title: "Contrato formal", body: "CDC + nota fiscal." },
  { icon: Wallet, title: "Parcelamento", body: "Em até 12x sem juros." },
  { icon: Star, title: "Companhias oficiais", body: "MSC, Costa, Royal, Norwegian, Disney." },
];

/** Página pública /cruzeiros — mesmo esqueleto visual de /intercambio.
 *  Os cards "Cruzeiros em Destaque" vêm de /api/cruzeiros/destaques (editáveis
 *  pelo admin em /admin/cruzeiros). */
export default function Cruzeiros() {
  const [destaques, setDestaques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  usePageMeta(
    "Cruzeiros · Viagens Orion",
    "Cruzeiros marítimos com contratação segura: MSC, Costa, Royal Caribbean, Norwegian. Transfer ida e volta de várias regiões de São Paulo. Roteiros pelo Brasil, Caribe, Mediterrâneo, Alaska e Fiordes.",
  );

  useEffect(() => {
    api.get("/cruzeiros/destaques").then(r => {
      setDestaques(r.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  // Redireciona pra checkout do cruzeiro passando pela tela de "pagamento seguro"
  const onGoCheckout = (url) => {
    if (!url) return;
    setRedirecting(true);
    setTimeout(() => { window.location.href = url; }, 2600);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {redirecting && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
          style={{
            background: "radial-gradient(ellipse at center, rgba(14, 2, 34, 0.94), rgba(4, 0, 14, 0.98))",
            backdropFilter: "blur(14px) saturate(140%)",
          }}
          data-testid="secure-redirect-overlay"
          role="status"
          aria-live="polite"
        >
          <div className="max-w-md w-full text-center rounded-2xl border border-emerald-400/30 bg-white/5 p-10 text-white shadow-2xl">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full border-[3px] border-emerald-200/20 border-t-emerald-300 animate-spin" />
            <h3 className="font-serif text-xl text-emerald-100 mb-3">Redirecionando para um ambiente de pagamento seguro…</h3>
            <p className="text-sm text-white/70 leading-relaxed">Aguarde alguns instantes. Estamos preparando sua conexão criptografada com o parceiro.</p>
          </div>
        </div>
      )}

      <main className="flex-1" data-testid="cruzeiros-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#062537] via-[#0d1e46] to-[#0a0d1e] text-white">
          <span className="absolute inset-0 opacity-30" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-b from-[#062537]/94 via-[#0d1e46]/72 to-[#0a0d1e]/85" aria-hidden />
          <span className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orion-gold/25 blur-3xl" aria-hidden />
          <span className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-cyan-500/20 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-4xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <Ship size={13} /> Viagens Orion · Cruzeiros
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mx-auto">
              O oceano cabe no seu <span className="text-orion-gold">próximo capítulo</span>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 mx-auto leading-relaxed">
              Cruzeiros com curadoria Orion — do temático nacional aos transatlânticos pelo Mediterrâneo,
              Caribe e Alaska. Contratação segura, consultor no WhatsApp durante toda a viagem
              e transfer ida e volta saindo de várias regiões de São Paulo.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a
                href="#destaques"
                className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                data-testid="cruz-cta-destaques"
              >
                <Anchor size={14} /> Ver cruzeiros em destaque
              </a>
              <button
                type="button"
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-gold hover:text-orion-gold transition-colors"
                data-testid="cruz-cta-consultor-hero"
              >
                Falar com consultor <ArrowRight size={14} />
              </button>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Contratação segura</span>
              <span className="inline-flex items-center gap-2"><Bus size={12} className="text-orion-gold" /> Transfer SP → Santos</span>
              <span className="inline-flex items-center gap-2"><Clock size={12} className="text-orion-gold" /> Suporte 24h a bordo</span>
              <span className="inline-flex items-center gap-2"><Wallet size={12} className="text-orion-gold" /> Até 12x sem juros</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`cruz-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILARES */}
        <section id="pilares" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Por que fechar com a Orion</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              O que o preço não mostra — mas faz toda a diferença.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              Cruzeiro barato demais quase sempre esconde: gorjeta não inclusa, transfer separado,
              seguro à parte, cabine acima do casino. A gente entrega o pacote inteiro fechado
              com cuidado de referência no segmento.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILARES.map((p, i) => (
              <div
                key={i}
                className="group relative overflow-hidden border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white text-center"
                data-testid={`cruz-pilar-${i}`}
              >
                <span className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-orion-orange/10 group-hover:bg-orion-orange/20 transition-colors" aria-hidden />
                <p.icon size={30} className="text-orion-orange mb-4 mx-auto group-hover:scale-110 transition-transform relative" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug relative">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed relative">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* MAIS PROCURADOS — vitrine editável (independente do sistema de cotações) */}
        <section id="destaques" className="bg-[#062537] text-white py-20 md:py-28 relative overflow-hidden">
          <span className="absolute -top-24 -left-24 w-[400px] h-[400px] rounded-full bg-orion-gold/15 blur-3xl" aria-hidden />
          <span className="absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full bg-cyan-500/15 blur-3xl" aria-hidden />
          <div className="relative container-orion">
            <div className="max-w-3xl mx-auto mb-14 text-center">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-4">Mais Procurados</div>
              <h2 className="font-serif text-[42px] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white">
                Vamos Realizar seu Sonho de Navegar em um Cruzeiro,
                <span className="text-orion-gold"> possuímos parcelamentos de até 30x</span>
              </h2>
              <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
                Descubra o Cruzeiro Ideal para Você, confira os <em className="text-orion-gold not-italic font-semibold uppercase tracking-wider text-sm md:text-base">mais procurados</em>{" "}
                ou solicite uma cotação para sua data disponível com <b>descontos de até 70% dos sites oficiais</b>.
                Temos Ótimas Oportunidades de Cruzeiros com Inventário baixo.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12 text-white/60">Carregando destaques…</div>
            ) : destaques.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-white/20 rounded-xl max-w-xl mx-auto">
                <Ship className="mx-auto text-white/40 mb-3" size={40} />
                <p className="text-white/70 mb-3">Nenhum destaque publicado ainda.</p>
                <button type="button" onClick={() => setQuoteOpen(true)} className="text-orion-gold text-sm underline hover:text-orion-orange" data-testid="cruz-cta-consultor-empty">
                  Fale com um consultor
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cruz-destaques-grid">
                {destaques.map((d) => (
                  <CruzeiroCard
                    key={d.id}
                    destaque={d}
                    onCheckout={onGoCheckout}
                    onOpenLightbox={setLightboxSrc}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Como funciona</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Da reserva ao retorno em SP — a gente resolve tudo.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW.map((h, i) => (
              <div key={i} className="border-l-2 border-orion-orange pl-5 py-2" data-testid={`cruz-how-${i}`}>
                <div className="font-mono text-orion-orange text-2xl mb-1">{h.step}</div>
                <h3 className="font-serif text-orion-purple text-lg mb-2">{h.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA FINAL — fundo creme claro Orion (mesma paleta do resto do site) */}
        <section className="bg-[#FAF7F0] py-16 border-t border-[#EFEAE0]">
          <div className="container-orion text-center max-w-2xl mx-auto">
            <MapPin className="mx-auto text-orion-orange mb-4" size={32} />
            <h2 className="font-serif text-3xl md:text-4xl mb-4 text-orion-purple">Pronto para embarcar?</h2>
            <p className="text-[#4a3a55] mb-8 leading-relaxed">
              Nosso consultor especializado em cruzeiros está no WhatsApp e responde em poucos minutos.
              Preparamos uma proposta com transfer ida e volta incluído.
            </p>
            <button
              type="button"
              onClick={() => setQuoteOpen(true)}
              className="inline-flex items-center gap-2 bg-orion-orange text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c96a1c] transition-all shadow-[0_14px_38px_rgba(216,109,44,0.35)] hover:-translate-y-0.5"
              data-testid="cruz-cta-consultor-final"
            >
              <Ship size={14} /> Falar com consultor
            </button>
          </div>
        </section>
      </main>

      {quoteOpen && <SchoolQuoteModal onClose={() => setQuoteOpen(false)} variant="cruzeiros" />}
      {lightboxSrc && <CruzeiroLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

      <Footer />
    </div>
  );
}
